import type { OfficeLayoutV1 } from "spatial-contracts";

export interface BoundingBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface CompiledOfficeLayout {
  width: number;
  height: number;
  tileSize: number;
  collisionBoxes: BoundingBox[];
  spawnPoint: { x: number; y: number };
  rooms: {
    id: string;
    name: string;
    cells: Set<string>;
  }[];
}

export function compileLayout(layout: OfficeLayoutV1): CompiledOfficeLayout {
  const { tileSize } = layout;
  const collisionBoxes: BoundingBox[] = [];

  // 1. Expand Wall Segments
  for (const wall of layout.walls) {
    if (wall.isDoor) {
      // Doors do not block movement in this slice (unlocked)
      continue;
    }

    const startX = Math.min(wall.startX, wall.endX);
    const endX = Math.max(wall.startX, wall.endX);
    const startY = Math.min(wall.startY, wall.endY);
    const endY = Math.max(wall.startY, wall.endY);

    // Cover every tile along the wall segment
    for (let tx = startX; tx <= endX; tx++) {
      for (let ty = startY; ty <= endY; ty++) {
        collisionBoxes.push({
          x: tx * tileSize,
          y: ty * tileSize,
          width: tileSize,
          height: tileSize,
        });
      }
    }
  }

  // 2. Expand Furniture collision footprints
  for (const item of layout.furniture) {
    const { collisionFootprint: fp } = item;
    collisionBoxes.push({
      x: item.x * tileSize + fp.offsetX * tileSize,
      y: item.y * tileSize + fp.offsetY * tileSize,
      width: fp.width * tileSize,
      height: fp.height * tileSize,
    });
  }

  // 3. Process Room Zone sets
  const rooms = layout.rooms.map((room) => {
    const cellSet = new Set<string>();
    for (const cell of room.cells) {
      cellSet.add(`${cell.x},${cell.y}`);
    }
    return {
      id: room.id,
      name: room.name,
      cells: cellSet,
    };
  });

  return {
    width: layout.width,
    height: layout.height,
    tileSize: layout.tileSize,
    collisionBoxes,
    spawnPoint: layout.spawnPoint,
    rooms,
  };
}
